/**
 * oneSignalService.js
 *
 * Wraps react-onesignal (Web SDK v16).
 *
 * Architecture:
 *  - OneSignal owns device ↔ user mapping via external_id = user.id.
 *  - Backend targets users via include_aliases: { external_id: [id] }.
 *
 * Login flow (safe on every page load):
 *  1. Guard: permission must be 'granted'.
 *  2. Guard: must have a real push subscription (optedIn=true + real token).
 *  3. logout() clears any stale/ghost user identity from localStorage.
 *  4. login(externalId) creates a clean user and links the subscription.
 */
import OneSignal from 'react-onesignal';
import { env } from '@/common/config';

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICE_WORKER_PATH = 'OneSignalSDKWorker.js';
const SERVICE_WORKER_SCOPE = '/';
const PENDING_ROUTE_KEY = 'onesignal_pending_route';

// ─── Module state ─────────────────────────────────────────────────────────────

let initPromise = null;
let initialized = false;
let missingAppIdLogged = false;
let loggedInExternalId = null; // null = not linked this session

/**
 * Reset the session login state so the next loginUser() call will
 * re-run OneSignal.login() even if we already linked this session.
 * Call this whenever the push subscription changes (e.g. autoResubscribe
 * regenerated a new token after a previous token expired).
 */
export function resetLoginState() {
  loggedInExternalId = null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** @returns {string} */
export function getBrowserName(userAgent = navigator.userAgent) {
  const ua = String(userAgent || '');
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\//i.test(ua) || /Opera/i.test(ua)) return 'Opera';
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  return 'Unknown';
}

/** @returns {'default' | 'granted' | 'denied'} */
export function getPermissionStatus() {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/** @returns {boolean} */
export function isPushSupported() {
  try {
    return OneSignal.Notifications.isPushSupported();
  } catch {
    return (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator
    );
  }
}

/** @returns {boolean} */
export function isConfigured() {
  return Boolean(env.oneSignalAppId?.trim());
}

/** @returns {boolean} */
export function isInitialized() {
  return (
    initialized ||
    (typeof window !== 'undefined' &&
      Boolean(window.OneSignal?.User?.PushSubscription))
  );
}

/**
 * Returns true when the current push subscription is real and opted-in.
 * A "local-" subscription ID is a placeholder that has NOT yet been synced
 * to OneSignal servers — calling login() with one causes 400 Bad Request.
 * @returns {boolean}
 */
export function hasRealSubscription() {
  try {
    const id = OneSignal.User?.PushSubscription?.id;
    const token = OneSignal.User?.PushSubscription?.token;
    const optedIn = OneSignal.User?.PushSubscription?.optedIn;
    if (!optedIn) return false;
    if (!token || !String(token).trim()) return false;
    if (id && String(id).startsWith('local-')) return false;
    return true;
  } catch {
    return false;
  }
}

/** @returns {boolean} */
export function hasPushToken() {
  try {
    const token = OneSignal.User?.PushSubscription?.token;
    return Boolean(token && String(token).trim());
  } catch {
    return false;
  }
}

// ─── Initialization ───────────────────────────────────────────────────────────

/**
 * Initialize OneSignal SDK once per page load.
 * Safe under React StrictMode (double invoke) due to initPromise singleton.
 * @returns {Promise<boolean>}
 */
export async function initOneSignal() {
  if (isInitialized()) {
    initialized = true;
    return true;
  }
  if (initPromise) return initPromise;

  if (!isConfigured()) {
    if (!missingAppIdLogged) {
      missingAppIdLogged = true;
      console.warn('[OneSignal] VITE_ONESIGNAL_APP_ID not set. Push disabled.');
    }
    return false;
  }

  // Safe pre-init self-healing: if previous session was corrupted (400 Bad Request / empty token loop),
  // purge the corrupt IndexedDB before OneSignal opens any connections to it.
  if (typeof window !== 'undefined' && typeof indexedDB !== 'undefined') {
    try {
      if (sessionStorage.getItem('onesignal_db_reset_needed') === 'true') {
        sessionStorage.removeItem('onesignal_db_reset_needed');
        indexedDB.deleteDatabase('ONE_SIGNAL_SDK_DB');
        console.info(
          '[OneSignal] Corrupt local DB purged cleanly before SDK init.',
        );
      }
    } catch {
      /* ignore */
    }
  }

  initPromise = (async () => {
    try {
      const initAction = OneSignal.init({
        appId: env.oneSignalAppId.trim(),
        allowLocalhostAsSecureOrigin: true,
        autoResubscribe: true,
        serviceWorkerPath: SERVICE_WORKER_PATH,
        serviceWorkerParam: { scope: SERVICE_WORKER_SCOPE },
        notificationClickHandlerMatch: 'origin',
        notificationClickHandlerAction: 'focus',
        welcomeNotification: { disable: true },
        promptOptions: {
          slidedown: { prompts: [{ type: 'push', autoPrompt: false }] },
        },
        ...(env.oneSignalSafariWebId?.trim()
          ? { safari_web_id: env.oneSignalSafariWebId.trim() }
          : {}),
      });

      // 7-second safety timeout so init never blocks or hangs UI indefinitely
      await Promise.race([
        initAction,
        new Promise((resolve) => setTimeout(resolve, 7000)),
      ]);

      initialized = true;

      try {
        const perm =
          typeof Notification !== 'undefined'
            ? Notification.permission
            : 'unknown';
        const subId = OneSignal.User?.PushSubscription?.id ?? null;
        const token = OneSignal.User?.PushSubscription?.token ?? null;
        const optedIn = OneSignal.User?.PushSubscription?.optedIn ?? false;
        console.info(
          `[OneSignal] Init ✓ | permission=${perm} | id=${subId} | token=${token ? '✓' : 'null'} | optedIn=${optedIn}`,
        );
      } catch {
        /* ignore */
      }

      try {
        OneSignal.User.PushSubscription.addEventListener('change', () => {
          try {
            const id = OneSignal.User?.PushSubscription?.id ?? null;
            const token = OneSignal.User?.PushSubscription?.token ?? null;
            const optedIn = OneSignal.User?.PushSubscription?.optedIn ?? false;
            console.info(
              `[OneSignal] Subscription ✓ | id=${id} | token=${token ? '✓' : 'null'} | optedIn=${optedIn}`,
            );
          } catch {
            /* ignore */
          }
        });
      } catch {
        /* ignore */
      }

      // If user previously chose to disable notifications, ensure SDK is opted out
      try {
        if (
          typeof localStorage !== 'undefined' &&
          localStorage.getItem('push_notifications_disabled') === 'true' &&
          OneSignal.User?.PushSubscription?.optedIn
        ) {
          await OneSignal.User.PushSubscription.optOut();
        }
      } catch {
        /* ignore */
      }

      return true;
    } catch (error) {
      const msg = (error?.message || String(error || '')).toLowerCase();
      if (msg.includes('already initialized')) {
        // SDK is already active in this window, treat as success
        initialized = true;
        return true;
      }
      initPromise = null;
      initialized = false;
      if (msg.includes('can only be used on')) {
        console.warn(
          `[OneSignal] Domain mismatch — ${msg}\n` +
            `Running on: "${window?.location?.origin ?? 'unknown'}"\n` +
            `Fix: Add this URL as the Site URL in your OneSignal dashboard → Settings → Web.`,
        );
      } else {
        console.error('[OneSignal] Init failed:', error);
      }
      return false;
    }
  })();

  return initPromise;
}

// ─── User identity ────────────────────────────────────────────────────────────

/**
 * Link the browser push subscription to an external user ID.
 *
 * Guards:
 *  1. Permission must be 'granted'.
 *  2. A real (non-local) push subscription must exist.
 *  3. Not already linked to the same ID this session.
 *
 * Calls logout() before login() to clear any stale ghost user ID cached in
 * localStorage from a previous broken session. This is the root cause of
 * the 400 Bad Request "existingOnesignalId" errors.
 *
 * @param {string | number} externalId
 * @param {Record<string, string | number | boolean>} [tags]
 */
export async function loginUser(externalId, tags = {}) {
  const ready = await initOneSignal();
  if (!ready) return;
  if (externalId == null) return;

  const rawId = String(externalId).trim();
  if (!rawId || rawId === 'undefined' || rawId === 'null' || rawId === '0')
    return;

  // Format ID with user_ prefix to prevent OneSignal from blocking
  // reserved single-digit IDs like "1" (which returns 400 "external_id is blocked").
  const id = rawId.startsWith('user_') ? rawId : `user_${rawId}`;

  const permissionGranted =
    typeof Notification !== 'undefined' &&
    Notification.permission === 'granted';

  const isExplicitlyDisabled =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('push_notifications_disabled') === 'true';

  const isOptedIn =
    !isExplicitlyDisabled && Boolean(OneSignal.User?.PushSubscription?.optedIn);
  const currentToken = OneSignal.User?.PushSubscription?.token;

  // If already linked this session AND currently matches state, safe to skip
  if (loggedInExternalId === id && isOptedIn && currentToken) return;

  // If SDK already reports this externalId AND is opted in with real token, safe to skip
  try {
    const currentExtId = OneSignal.User?.externalId;
    if (
      currentExtId &&
      String(currentExtId) === id &&
      isOptedIn &&
      currentToken
    ) {
      loggedInExternalId = id;
      return;
    }
  } catch {
    /* ignore — SDK may not be fully initialized yet */
  }

  // If browser permission is granted and user is not disabled, ensure we wait
  // for Chrome to generate the FCM push token BEFORE calling OneSignal.login(id).
  // Calling login() while token is still null / "" causes OneSignal Cloud to reject
  // the subscription update with a 400 Bad Request ("token": "").
  if (permissionGranted && !isExplicitlyDisabled && !hasPushToken()) {
    console.info('[OneSignal] Waiting for push token before login...');
    await waitForPushToken({ timeoutMs: 5000 });
  }

  // Optimistic lock: set ID before async operations so that subscription-change
  // events cannot trigger a second concurrent loginUser() call.
  // Reset to null on failure.
  loggedInExternalId = id;

  try {
    console.log(`[OneSignal] login called with ID: ${id}`);

    // Call OneSignal.login(id) only if OneSignal.User.externalId differs from id
    const currentExtId = OneSignal.User?.externalId;
    if (!currentExtId || String(currentExtId) !== id) {
      await OneSignal.login(id);
    }
    loggedInExternalId = id;

    // If user explicitly disabled notifications, ensure opted-out state is maintained
    if (isExplicitlyDisabled && OneSignal.User?.PushSubscription?.optedIn) {
      try {
        await OneSignal.User.PushSubscription.optOut();
      } catch {
        /* ignore */
      }
    }

    const optedInAfter =
      !isExplicitlyDisabled &&
      Boolean(OneSignal.User?.PushSubscription?.optedIn);
    const subId = OneSignal.User?.PushSubscription?.id;
    console.log(
      `[OneSignal] login success | external_id=${id} | optedIn=${optedInAfter} | subscriptionId=${subId}`,
    );

    // Set optional user tags for segmentation.
    const sanitized = {};
    Object.entries(tags || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      sanitized[key] = String(value);
    });
    if (Object.keys(sanitized).length > 0) {
      try {
        await OneSignal.User.addTags(sanitized);
      } catch {
        /* tag failure is non-fatal */
      }
    }
  } catch (error) {
    loggedInExternalId = null;

    const isUndefinedError = error === undefined || error === null;
    const msg = isUndefinedError
      ? ''
      : (error?.message || String(error)).toLowerCase();

    if (
      isUndefinedError ||
      msg.includes('existingonesignalid') ||
      msg.includes('400') ||
      msg.includes('404')
    ) {
      // Flag the corruption for the NEXT page load's purgeCorruptDbIfNeeded()
      // We don't delete DB while SDK is active because SDK will re-open it.
      sessionStorage.setItem('onesignal_db_reset_needed', 'true');
      console.warn(
        '[OneSignal] Detected corrupt local state (400/404/Ghost user). Flagged for auto-purge on next page load.',
      );
    }
    console.error('[OneSignal] login failed:', error);
  }
}

/** Remove external_id link / unlink device on app logout. */
export async function logoutUser() {
  loggedInExternalId = null;
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('push_notifications_disabled');
  }

  // Skip if OneSignal is not initialized
  if (!isInitialized()) {
    return;
  }

  try {
    if (typeof OneSignal?.logout === 'function') {
      await Promise.race([
        OneSignal.logout(),
        new Promise((resolve) => setTimeout(resolve, 1000)),
      ]);
      console.info('[OneSignal] logout: user identity unlinked successfully.');
    }
  } catch (error) {
    console.warn('[OneSignal] logout failed:', error?.message || error);
  }
}

// ─── Permission ───────────────────────────────────────────────────────────────

/**
 * Request browser notification permission. MUST be called from a user gesture.
 *
 * Flow:
 *  1. Native Notification.requestPermission() → shows Chrome's permission dialog.
 *  2. If denied/dismissed → return early.
 *  3. OneSignal.User.PushSubscription.optIn() → registers with FCM/APNS servers.
 *
 * @returns {Promise<'default' | 'granted' | 'denied'>}
 */
export async function requestPermission() {
  const ready = await initOneSignal();
  if (!ready) return getPermissionStatus();

  try {
    if (typeof OneSignal.User?.PushSubscription?.optIn === 'function') {
      // In v16, optIn() will automatically prompt the user if permission is 'default',
      // and will register the service worker and sync the push token.
      await OneSignal.User.PushSubscription.optIn();
    }
  } catch (error) {
    const msg = (error?.message || String(error || '')).toLowerCase();
    if (!msg.includes('abort') && !msg.includes('connection was closed')) {
      console.warn('[OneSignal] optIn() error:', error?.message || error);
    }
  }

  return getPermissionStatus();
}

/**
 * Opt out / disable push notifications for this device.
 * @returns {Promise<boolean>}
 */
export async function disableNotifications() {
  const ready = await initOneSignal();
  if (!ready) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('push_notifications_disabled', 'true');
    }
    if (typeof OneSignal.User?.PushSubscription?.optOut === 'function') {
      await OneSignal.User.PushSubscription.optOut();
      console.info('[OneSignal] Push notifications opted out.');
      return true;
    }
  } catch (error) {
    console.warn('[OneSignal] optOut() error:', error?.message || error);
  }
  return false;
}

/**
 * Opt in / enable push notifications for this device.
 * @returns {Promise<boolean>}
 */
export async function enableNotifications() {
  const ready = await initOneSignal();
  if (!ready) return false;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('push_notifications_disabled');
    }
    if (typeof OneSignal.User?.PushSubscription?.optIn === 'function') {
      await OneSignal.User.PushSubscription.optIn();
      console.info('[OneSignal] Push notifications opted in.');
      return true;
    }
  } catch (error) {
    console.warn('[OneSignal] optIn() error:', error?.message || error);
  }
  return false;
}

/**
 * Check if notifications are actively opted in.
 * @returns {boolean}
 */
export function isOptedIn() {
  try {
    if (
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('push_notifications_disabled') === 'true'
    ) {
      return false;
    }
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission !== 'granted'
    ) {
      return false;
    }
    if (typeof OneSignal.User?.PushSubscription?.optedIn === 'boolean') {
      return OneSignal.User.PushSubscription.optedIn;
    }
    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ─── Subscription helpers ─────────────────────────────────────────────────────

/**
 * Wait up to timeoutMs for a real push subscription to appear.
 * @param {{ timeoutMs?: number }} [options]
 * @returns {Promise<boolean>}
 */
export function waitForPushToken({ timeoutMs = 15000 } = {}) {
  if (hasRealSubscription()) return Promise.resolve(true);
  if (!initialized) return Promise.resolve(false);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        OneSignal.User.PushSubscription.removeEventListener('change', onChange);
      } catch {
        /* ignore */
      }
      resolve(ok);
    };

    const onChange = () => {
      if (hasRealSubscription()) finish(true);
    };
    const timer = setTimeout(() => finish(hasRealSubscription()), timeoutMs);

    try {
      OneSignal.User.PushSubscription.addEventListener('change', onChange);
    } catch {
      finish(false);
    }
  });
}

/** @returns {{ subscriptionId: string|null, permissionStatus: string, optedIn: boolean, browser: string, userAgent: string, platform: string }} */
export function getSubscriptionSnapshot() {
  let subscriptionId = null;
  let optedIn = false;
  try {
    subscriptionId = OneSignal.User?.PushSubscription?.id || null;
    optedIn = Boolean(OneSignal.User?.PushSubscription?.optedIn);
  } catch {
    /* ignore */
  }

  return {
    subscriptionId,
    permissionStatus: getPermissionStatus(),
    optedIn,
    browser: getBrowserName(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform: 'web',
  };
}

// ─── Event listeners ──────────────────────────────────────────────────────────

/** @returns {() => void} */
export function onSubscriptionChange(handler) {
  if (!initialized || typeof handler !== 'function') return () => {};
  OneSignal.User.PushSubscription.addEventListener('change', handler);
  return () => {
    try {
      OneSignal.User.PushSubscription.removeEventListener('change', handler);
    } catch {
      /* ignore */
    }
  };
}

/** @returns {() => void} */
export function onPermissionChange(handler) {
  if (!initialized || typeof handler !== 'function') return () => {};
  OneSignal.Notifications.addEventListener('permissionChange', handler);
  return () => {
    try {
      OneSignal.Notifications.removeEventListener('permissionChange', handler);
    } catch {
      /* ignore */
    }
  };
}

/** @returns {() => void} */
export function onForegroundWillDisplay(handler) {
  if (!initialized || typeof handler !== 'function') return () => {};
  OneSignal.Notifications.addEventListener('foregroundWillDisplay', handler);
  return () => {
    try {
      OneSignal.Notifications.removeEventListener(
        'foregroundWillDisplay',
        handler,
      );
    } catch {
      /* ignore */
    }
  };
}

/** @returns {() => void} */
export function onNotificationClick(handler) {
  if (!initialized || typeof handler !== 'function') return () => {};
  OneSignal.Notifications.addEventListener('click', handler);
  return () => {
    try {
      OneSignal.Notifications.removeEventListener('click', handler);
    } catch {
      /* ignore */
    }
  };
}

// ─── Payload normalization ────────────────────────────────────────────────────

/** @param {any} eventOrNotification */
export function normalizePushPayload(eventOrNotification) {
  const notification =
    eventOrNotification?.notification ||
    eventOrNotification?.result?.notification ||
    eventOrNotification ||
    {};

  let additionalData =
    notification.additionalData ||
    notification.additional_data ||
    notification.data ||
    {};

  if (typeof additionalData === 'string') {
    try {
      additionalData = JSON.parse(additionalData);
    } catch {
      additionalData = {};
    }
  }

  let payload = additionalData.payload || additionalData;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      payload = {};
    }
  }

  const parsedData = coerceNumericIds(additionalData);
  const parsedPayload = coerceNumericIds(payload);

  const launchURL =
    notification.launchURL ||
    notification.launchUrl ||
    notification.url ||
    additionalData.launchURL ||
    additionalData.url ||
    payload?.url ||
    payload?.link ||
    null;

  const id =
    notification.notificationId ||
    notification.id ||
    additionalData.notificationId ||
    additionalData.id ||
    payload?.id ||
    null;

  return {
    id,
    notificationId: id,
    title: notification.title || additionalData.title || payload?.title || '',
    message:
      notification.body ||
      notification.message ||
      additionalData.message ||
      additionalData.body ||
      payload?.message ||
      payload?.body ||
      '',
    notification_type:
      additionalData.notification_type ||
      additionalData.type ||
      additionalData.event ||
      payload?.notification_type ||
      payload?.type ||
      payload?.event ||
      '',
    link: typeof launchURL === 'string' ? launchURL : undefined,
    data: parsedData,
    payload: parsedPayload,
    ...parsedData,
    ...(parsedPayload && typeof parsedPayload === 'object'
      ? parsedPayload
      : {}),
  };
}

function coerceNumericIds(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;
  const next = { ...value };
  for (const key of Object.keys(next)) {
    const raw = next[key];
    if (
      (key === 'id' || key.endsWith('_id')) &&
      typeof raw === 'string' &&
      /^\d+$/.test(raw)
    ) {
      next[key] = Number(raw);
    }
  }
  return next;
}

// ─── Pending route ────────────────────────────────────────────────────────────

/** @param {string} path @param {object} [state] */
export function storePendingRoute(path, state) {
  if (!path) return;
  try {
    sessionStorage.setItem(
      PENDING_ROUTE_KEY,
      JSON.stringify({ path, state: state || null }),
    );
  } catch {
    /* ignore */
  }
}

/** @returns {{ path: string, state: object|null }|null} */
export function consumePendingRoute() {
  try {
    const raw = sessionStorage.getItem(PENDING_ROUTE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_ROUTE_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed?.path) return null;
    return { path: parsed.path, state: parsed.state || null };
  } catch {
    return null;
  }
}

// ─── Default export ───────────────────────────────────────────────────────────

const oneSignalService = {
  init: initOneSignal,
  login: loginUser,
  logout: logoutUser,
  resetLoginState,
  requestPermission,
  disableNotifications,
  enableNotifications,
  isOptedIn,
  hasPushToken,
  hasRealSubscription,
  waitForPushToken,
  getSubscriptionSnapshot,
  getPermissionStatus,
  getBrowserName,
  isPushSupported,
  isConfigured,
  isInitialized,
  onSubscriptionChange,
  onPermissionChange,
  onForegroundWillDisplay,
  onNotificationClick,
  normalizePushPayload,
  storePendingRoute,
  consumePendingRoute,
};

export default oneSignalService;
